<template>
  <div :class="{ 'subscribe-to-ai-visibility-modal': true, update: update }">
    <div v-if="getPackagesLoading" class="modal-loading">
      <loader />
    </div>
    <span class="kw kw-x" @click="close" />
    <div class="info-block">
      <p class="title">
        <span v-if="!update">{{ $t('take-control-ai-visibility') }}</span>
        <span v-else>{{ $t('update-ai-visibility-package') }}</span>
      </p>
      <template v-if="!update">
        <p class="desc" v-html="$t('take-control-ai-visibility-desc')" />

        <div class="preview-block">
          <video width="322" height="178" controls poster="~img/ai-visibility-thumb.png">
            <source :src="videoUrl" type="video/mp4" />
          </video>
        </div>
      </template>
      <div class="choose-credits-block">
        <p>{{ $t('choose-your-monthly-credits') }}</p>
        <custom-v-select
          id="choose_credits"
          options-label="name"
          options-value="value"
          search-key="name"
          data-cy="choose_credits"
          placeholder=""
          :search="false"
          :options="creditsList"
          :value.sync="credits"
          dropdown-menu-data-cy="credits-list"
          dropdown-search-data-cy="credits-search"
          @change="onChange($event)"
          disableDeSelect
        />
      </div>

      <kw-button
        :button-text="getSubmitButtonText"
        :button-classes="'rankscale'"
        button-type="primary"
        @click="submit"
        :loading="loading"
      />
    </div>
    <div class="side-block">
      <p v-if="!update" class="title">
        {{ $t('how-it-works') }}
      </p>
      <div class="list-wrapper">
        <ul v-if="!update">
          <li>Credits are used every time you refresh rankings.</li>
          <li>
            Each refresh counts as one run, and the number of credits used per run depends on which AI search engines
            you’re tracking.
            <span
              @mouseover="
                tooltip({ text: creditsTooltip, event: $event, interactive: true, arrow: false, maxWidth: 251 })
              "
              class="kw kw-info"
            />
          </li>
          <li>You choose how often to run updates — daily, weekly, or manually.</li>
          <li>Unused credits expire at the end of each month, so plan your updates to make the most of them.</li>
        </ul>
      </div>
      <div class="pricing-info-wrapper">
        <div class="info-row">
          <p>{{ credits.toLocaleString('en-US') }} credits</p>
          <p>$ {{ price }}/mo</p>
        </div>
        <template v-if="update">
          <div class="info-row">
            <p><b>Due on next renewal*</b></p>
            <p>$ {{ getDiscounted }}/mo</p>
          </div>
        </template>
        <template v-else>
          <div class="info-row">
            <p><b>Total due now*</b></p>
            <p>$ {{ getDiscounted }}/mo</p>
          </div>
        </template>

        <p class="pricing-hint">*Prices are exclusive of applicable taxes</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ClickOutside from 'vue-click-outside';
import ToolTipRenderMixin from '@/mixins/ToolTipRenderMixin';
import { aiVisibilityCreditsInfo, aiVisibilityCredits } from '@/constants';
import toastr from '@/helpers/init-toastr';
import posthog from 'posthog-js';
import { isAiPackage } from '~/views/auth/billing-utils';

export default {
  name: 'SubscribeToAiVisibilityModal',
  mixins: [ToolTipRenderMixin],
  directives: {
    ClickOutside,
  },
  props: {
    update: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      credits: 50,
      creditsTooltipList: aiVisibilityCreditsInfo,
      creditsList: aiVisibilityCredits,
      videoUrl:
        posthog.getFeatureFlag('ai-visibility-video') === 'ben'
          ? 'https://44592789.fs1.hubspotusercontent-na1.net/hubfs/44592789/Discover%20AI%20Visibility.mp4'
          : 'https://44592789.fs1.hubspotusercontent-na1.net/hubfs/44592789/AI%20Visibility%20announcement-1.mp4',
    };
  },
  computed: {
    ...mapGetters(['getPackages', 'getPackagesLoading', 'getAiVisibilitySubscription', 'getCurrentPlan']),
    creditsTooltip() {
      const rows = this.creditsTooltipList
        .map(
          (item) => `
                <div class="t-row">
                    <span>${item.name}</span>
                    <span>${item.value}</span>
                </div>
            `,
        )
        .join('');

      return `
                <div class="credits-custom-tooltip">
                    <p class="title">Number credits used per Run</p>
                    ${rows}
                    <a href="https://support.keyword.com/en/articles/11064208-how-credits-work" target="_blank" class="t-link"><span class="kw kw-info"></span>Read More</a>
                </div>`;
    },
    price() {
      return this.creditsList.find((item) => item.value === this.credits)?.price || 0;
    },
    getDiscounted() {
      return this.price || 0;
    },
    getSubmitButtonText() {
      return this.update ? this.$t('upgrade-subscription') : this.$t('subscribe-to-ai-visibility');
    },
  },
  mounted() {
    this.fetchPackages();
    if (this.getAiVisibilitySubscription) {
      this.credits = this.getAiVisibilitySubscription.credits;
    }
  },
  methods: {
    ...mapActions(['subscribeToAiVisibility', 'fetchPackages']),
    submit() {
      const aiVisibilityPackage = this.getPackages.find(isAiPackage);
      if (aiVisibilityPackage) {
        this.loading = true;
        this.subscribeToAiVisibility({
          plan_id: aiVisibilityPackage.package_identity,
          quantity: this.credits,
        })
          .then((res) => {
            if (res) this.close();
            if (!this.update) {
              this.$router.push('/ai-visibility/');
            }
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        toastr.e(this.$t('unexpected-error'));
      }
    },
    onChange(val) {
      this.credits = val;
    },
    close() {
      this.$emit('close');
    },
    upgradePlan() {
      this.$router.push({ name: 'Pricing' });
      this.close();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~sass/modal';
.subscribe-to-ai-visibility-modal {
  position: relative;
  width: 100%;
  display: flex;
  &.update {
    .choose-credits-block {
      margin-bottom: 32px;
      margin-top: 24px;
    }
    .info-block {
      padding: 23px 25px 27px;
    }
    .pricing-info-wrapper {
      margin-bottom: 32px;
    }
  }
  .info-block {
    flex: 1;
    padding: 23px 25px 14px;
    background-color: #fff;
    p.title {
      font-family: Roboto;
      font-weight: 500;
      font-size: 18px;
      line-height: 100%;
    }
    p.desc {
      font-family: Roboto;
      font-weight: 400;
      font-size: 13px;
      line-height: 16px;
      ::v-deep a.link {
        font-weight: 700;
        color: kw-color(kw-purple, 10);
      }
    }
    & > button.rankscale {
      width: 100%;
      height: 36px;
      margin-top: 24px;
    }
  }

  .upgrade-block {
    padding-top: 16px;
    font-weight: 400;
    font-size: 13px;
    line-height: 100%;
    .head {
      margin: 0 0 4px;
      color: kw-color(kw-purple, 11);
      font-weight: 500;
    }
  }

  .preview-block {
    height: 178px;
    border-radius: 9px;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }

  .choose-credits-block {
    margin: 17px 0;
    ::v-deep ul.dropdown-list {
      max-height: 160px;
      @media screen and (max-height: 700px) and (min-width: 600px) {
        max-height: 70px;
      }
    }
    & > p {
      font-family: Roboto;
      font-weight: 500;
      font-size: 13px;
      line-height: 20px;
      margin: 0 0 4px;
    }
    .btn-group {
      width: 100%;
      ::v-deep .form-control.dropdown-toggle {
        border: 1px solid #dee5ec !important;
      }
    }
  }

  .side-block {
    width: 268px;
    padding: 26px 24px;
    background-color: kw-color(kw-purple, 10);
    color: kw-color(kw-white, 1);
    display: flex;
    flex-direction: column;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
    p.title {
      font-family: Roboto;
      font-weight: 700;
      font-size: 13px;
      line-height: 16px;
    }
    .list-wrapper {
      flex: 1;
      ::v-deep ul {
        margin-left: -24px;
        font-family: Roboto;
        font-weight: 400;
        font-size: 13px;
        line-height: 16px;
        letter-spacing: 0;
      }
    }
  }

  .kw-x {
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    top: 7px;
    right: 7px;
    font-size: 10px;
    width: 16px;
    height: 16px;
    cursor: pointer;
    font-weight: 900;
    color: kw-color(kw-light-grey, 4);
  }
  .pricing-info-wrapper {
    .info-row {
      width: 100%;
      display: flex;
      justify-content: space-between;
      border-bottom: 1px solid #c1c4cd;
      font-family: Aeonik;
      font-weight: 400;
      font-size: 14px;
      line-height: 100%;
      letter-spacing: 0%;
      padding-bottom: 8px;
      margin-bottom: 4px;
      p {
        margin: 0;
        b {
          font-weight: 500;
        }
      }
    }
    .pricing-hint {
      margin: 4px 0 0;
      font-family: Aeonik;
      font-weight: 400;
      font-size: 10px;
      line-height: 100%;
      letter-spacing: 0%;
    }
  }
  .modal-loading {
    position: absolute;
    z-index: 999;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.5;
    background: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  @media screen and (max-height: 520px) {
    height: 100%;
  }
  @media screen and (max-width: 500px) {
    flex-direction: column;
    .side-block {
      width: 100%;
      border-radius: 0;
    }
  }
}
::v-deep .credits-custom-tooltip {
  text-align: left;
  padding: 8px 10px;
  width: 248px;
  p {
    font-family: Roboto;
    font-size: 13px;
    line-height: 100%;
    letter-spacing: 0%;
    margin: 0;

    &.title {
      font-weight: 700;
      color: #9136f9;
      margin-bottom: 10px;
    }
  }
  .t-row {
    width: 100%;
    margin-left: 3px;
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid #d7d9df;
    span {
      font-size: 11px;
      &:nth-child(2) {
        font-weight: 500;
      }
    }
  }
  a.t-link {
    display: block;
    font-family: Roboto;
    font-weight: 500;
    font-size: 12px;
    line-height: 100%;
    letter-spacing: 0;
    margin-top: 6px;
    .kw-info {
      color: kw-color(kw-purple, 10);
      margin-right: 9px;
    }
    color: kw-color(kw-purple, 10);
  }
}
</style>
